import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Spinner } from "@/assets/svg/Spinner";
import { InviteSuccess } from "./InviteSuccess";
import { getCsrfToken } from "@/util/getCsrfToken";
import { constants } from "@/constants";
import { AcceptForm } from "./AcceptForm";
import { AcceptInviteFields, InviteData } from "./types";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { AlertsWrapper } from "@/components/AlertsWrapper";
import { ApiError } from "@/api/authApiClient";

export function AcceptInvite() {
  const { inviteToken } = useParams<{ inviteToken: string | undefined }>();
  const [invite, setInvite] = useState<InviteData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<ApiError[]>([]);
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const phoneValidationCode = searchParams.get("p");
  const emailValidationCode = searchParams.get("e");

  useEffect(() => {
    if (!inviteToken) {
      return;
    }
    const fetchInvite = async (inviteToken: string) => {
      setLoading(true);
      const config = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken() || "",
        },
      };
      const response = await fetch(
        `${constants.ZEEMEE_PUBLIC_API_URL}/dash_invites/${inviteToken}`,
        config,
      );

      if (response.ok) {
        setInvite((await response.json()).dash_invite);
        setLoading(false);
      } else {
        toast.error(t("acceptInvitation.errors.support.title"), {
          description: t("acceptInvitation.errors.support.description"),
        });
      }
    };
    fetchInvite(inviteToken);
  }, [inviteToken, t]);

  const acceptInvite = async (data: AcceptInviteFields) => {
    const config = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken() || "",
      },
      body: JSON.stringify({
        dash_invite: {
          email_validation_code: emailValidationCode,
          phone_validation_code: phoneValidationCode,
          first_name: data.firstName,
          last_name: data.lastName,
          password: data.password,
          password_confirm: data.passwordConfirm,
        },
      }),
    };
    const response = await fetch(
      `${constants.ZEEMEE_PUBLIC_API_URL}/dash_invites/${inviteToken}/accept`,
      config,
    );

    if (response.ok) {
      setSuccess(true);
    } else {
      const jsonBody = await response.json();
      const hasPasswordError = jsonBody.errors.some(
        ({ message }: { message: string }) =>
          message.toLowerCase().includes("password"),
      );
      if (hasPasswordError) {
        setErrors([{ code: constants.ERROR_CODES["WEAK_PASSWORD"] }]);
      } else {
        toast.error(t("acceptInvitation.errors.support.title"), {
          description:
            jsonBody.errors
              ?.map(({ message }: { message: string }) => message)
              .join(", ") || t("acceptInvitation.errors.support.description"),
        });
      }
    }
  };

  if (loading) {
    return <Spinner className="text-primary/50" />;
  }

  return success || invite?.is_accepted ? (
    <InviteSuccess orgName={invite?.organization?.name ?? ""} />
  ) : (
    <main role="main">
      <AlertsWrapper errors={errors} messages={[]}>
        <AcceptForm invite={invite} onSubmit={acceptInvite} />
      </AlertsWrapper>
    </main>
  );
}
